import { forwardRef, useCallback, useEffect, useState, type ComponentProps, type ChangeEvent, type FocusEvent } from 'react'
import { formatTrNumberDisplay, toCanonicalNumber } from '@/lib/numberFormat'
import { cn } from '@/lib/utils'

export type NumberInputProps = Omit<ComponentProps<'input'>, 'type' | 'value' | 'onChange' | 'defaultValue' | 'inputMode'> & {
  /** Kanonik değer: `85000.50` */
  value?: string
  onChange?: (canonical: string) => void
  maxDecimals?: number
  /** Blur’da ondalığı sabitler (örn. 2 → ,00) */
  minDecimalsOnBlur?: number
  allowNegative?: boolean
  prefix?: string
  suffix?: string
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value = '',
    onChange,
    maxDecimals = 2,
    minDecimalsOnBlur,
    allowNegative = false,
    prefix,
    suffix,
    className,
    onBlur,
    disabled,
    placeholder,
    ...props
  },
  ref,
) {
  const [display, setDisplay] = useState(() =>
    value ? formatTrNumberDisplay(value, { maxDecimals }) : '',
  )

  useEffect(() => {
    const next = value ? formatTrNumberDisplay(value, { maxDecimals }) : ''
    setDisplay((prev) => {
      // Aynı kanonik değerken yazmayı bozma
      const prevCanon = toCanonicalNumber(prev, { maxDecimals })
      if (prevCanon === (value || '')) return prev
      return next
    })
  }, [value, maxDecimals])

  const emit = useCallback(
    (rawDisplay: string) => {
      let text = rawDisplay
      if (!allowNegative) text = text.replace(/-/g, '')
      const formatted = formatTrNumberDisplay(text, { maxDecimals })
      setDisplay(formatted)
      onChange?.(toCanonicalNumber(formatted, { maxDecimals }))
    },
    [allowNegative, maxDecimals, onChange],
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    emit(e.target.value)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (minDecimalsOnBlur != null && display) {
      const canon = toCanonicalNumber(display, { maxDecimals })
      if (canon) {
        const [i = '0', d = ''] = canon.split('.')
        const padded = `${i}.${d.padEnd(minDecimalsOnBlur, '0').slice(0, maxDecimals)}`
        const shown = formatTrNumberDisplay(padded, { maxDecimals, minDecimals: minDecimalsOnBlur })
        setDisplay(shown)
        onChange?.(toCanonicalNumber(shown, { maxDecimals }))
      }
    }
    onBlur?.(e)
  }

  return (
    <div className={cn('relative', disabled && 'opacity-50')}>
      {prefix ? (
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs font-medium text-muted-foreground">
          {prefix}
        </span>
      ) : null}
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-card py-1 text-sm tabular-nums shadow-xs transition-colors',
          'placeholder:text-muted-foreground',
          'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/30',
          prefix ? 'pl-10' : 'pl-3',
          suffix ? 'pr-10' : 'pr-3',
          className,
        )}
        {...props}
      />
      {suffix ? (
        <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs font-medium text-muted-foreground">
          {suffix}
        </span>
      ) : null}
    </div>
  )
})
